import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { listApplications } from "@empite/api-client";
import type { ApplicationList } from "@empite/api-client";
import { useTransport } from "@empite/session";

/**
 * One application this customer has money in, as the portal needs to name it.
 *
 * The currency is deliberately absent even though the control-plane list now
 * carries one: every billing route treats an omitted currency as "the platform
 * default" and answers with the currency the wallet actually holds, and a
 * customer funded in two currencies for one application is ONE card here with
 * two wallets behind it. Pinning a currency on the card would silently pick one
 * of them.
 */
export interface PortalApplication {
  id: string;
  slug: string;
  name: string;
  /**
   * The company that publishes it.
   *
   * This list is NOT scoped to one company — the control plane resolves it
   * from the subject's wallets, whoever owns the application — so without the
   * publisher two products from two companies arrive as flat siblings that
   * only their independently chosen names tell apart. `companyName` is what
   * the screen groups by; `companySlug` is the stable key it groups ON, since
   * two companies may display the same name.
   */
  companySlug: string;
  companyName: string;
}

/**
 * The applications one company publishes, as the home screen renders them.
 *
 * A group rather than a flat list with a repeated label, because "who am I
 * paying" is the question a person brings to a page of balances, and a heading
 * answers it once for the cards under it.
 */
export interface CompanyApplications {
  slug: string;
  name: string;
  applications: PortalApplication[];
}

/**
 * Group applications by their publisher, keeping the order they arrived in.
 *
 * The control plane already orders by company and then by application, so
 * preserving arrival order here means the browser renders the order the
 * database chose rather than inventing a second one that could disagree with
 * it after a rename.
 */
export function groupByCompany(applications: PortalApplication[]): CompanyApplications[] {
  const groups = new Map<string, CompanyApplications>();
  for (const app of applications) {
    const existing = groups.get(app.companySlug);
    if (existing) {
      existing.applications.push(app);
      continue;
    }
    groups.set(app.companySlug, {
      slug: app.companySlug,
      // The slug is the fallback, not "Unknown": a row always carries a
      // company (the join is INNER on a NOT NULL column), so an empty name is
      // a company that has not set one rather than an application with no
      // publisher.
      name: app.companyName || app.companySlug,
      applications: [app],
    });
  }
  return [...groups.values()];
}

/**
 * Where the list comes from, so a screen can say so.
 *
 * "platform" is the durable answer: GET /platform/v1/applications resolves the
 * wallets from the SSO session's own subject, so the browser is never told an
 * application id out of band. "configured" remains a local-development escape
 * hatch for an operator running the stack by hand against a database with no
 * wallets seeded yet — it is checked first precisely because it exists to
 * override.
 */
export type ApplicationSource = "configured" | "platform";

export interface CustomerApplications {
  applications: PortalApplication[];
  source: ApplicationSource;
  isPending: boolean;
  error: unknown;
}

/** The query key. Not under billingKeys: this list is the same for every
 *  application, and putting it under one application's key would make a
 *  top-up in that application invalidate the whole navigation. */
export const applicationsKey = ["applications"] as const;

/**
 * VITE_PORTAL_APPLICATIONS is a JSON array of {id, slug, name}.
 *
 * Parsed once at module load and never thrown from: a malformed override in
 * someone's .env.local must not take the whole portal down, it must fall back
 * to what the platform reports.
 */
const configured: PortalApplication[] = parseConfigured(import.meta.env.VITE_PORTAL_APPLICATIONS);

function parseConfigured(raw: unknown): PortalApplication[] {
  if (typeof raw !== "string" || raw.trim() === "") {
    return [];
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    // The override predates the company on this row and is a hand-written
    // developer escape hatch, so it is normalised rather than required to
    // carry one: an entry with no company groups under a heading that says so,
    // which is more useful than a blank heading and far more useful than the
    // whole portal failing to render because a local .env is out of date.
    return parsed.filter(isApplication).map((row) => ({
      ...row,
      companySlug: row.companySlug || "configured",
      companyName: row.companyName || "Configured locally",
    }));
  } catch {
    return [];
  }
}

function isApplication(value: unknown): value is PortalApplication {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return typeof row.id === "string" && row.id !== "" && typeof row.slug === "string";
}

/**
 * useCustomerApplications answers "which applications should this customer
 * see a wallet card for".
 *
 * It asks the control plane, which resolves the answer from the wallets the
 * signed-in account owns. That is the whole of the fix for the device-binding
 * approximation this used to be: a customer granted credit for an application
 * they have never installed now has a card, and an application another
 * customer funded was never in the answer to begin with.
 */
export function useCustomerApplications(): CustomerApplications {
  const transport = useTransport();
  const listed = useQuery({
    queryKey: applicationsKey,
    queryFn: (): Promise<ApplicationList> => listApplications(transport),
    // A configured override is already an answer, so a machine that supplied
    // one must not wait on — or fail on — a request it is overriding.
    enabled: configured.length === 0,
  });

  const applications = useMemo(() => {
    if (configured.length > 0) {
      return configured;
    }
    return fromPlatform(listed.data);
  }, [listed.data]);

  return {
    applications,
    source: configured.length > 0 ? "configured" : "platform",
    isPending: configured.length > 0 ? false : listed.isPending,
    error: configured.length > 0 ? null : listed.error,
  };
}

/** One card per APPLICATION, though the route answers one row per WALLET: an
 *  application funded in USD and GBP is two balances and one application, and
 *  the cards behind this list read every currency the customer holds. The
 *  route already orders by name, so the first row for an id wins and the
 *  insertion order is the display order. */
function fromPlatform(list: ApplicationList | undefined): PortalApplication[] {
  const byID = new Map<string, PortalApplication>();
  for (const row of list?.applications ?? []) {
    if (byID.has(row.id)) {
      continue;
    }
    byID.set(row.id, {
      id: row.id,
      slug: row.slug,
      name: row.name || row.slug,
      companySlug: row.company_slug,
      companyName: row.company_name,
    });
  }
  return [...byID.values()];
}
